import { Dispatch, SetStateAction, useState } from 'react'
import {
  Button,
  IconButton,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'

import { AddLanguageButton } from '../add-language-button'
import { UpdateLanguageModal } from '../update-language-form'
import { useMe, useRemoveLanguages, useTableItemsCheck } from 'src/hooks'
import { ILanguageBaseWithId } from 'src/types'

import { getTableTimestamp } from 'src/common/utils'
import { DEFAULT_LANGUAGE_CODE } from '../constants'
import { HelperWrapper } from 'src/components/helper-wrapper'

interface IProps {
  items?: void | ILanguageBaseWithId[]
  sort: string
  setSort: Dispatch<SetStateAction<string>>
}

const LanguagesTable = ({ items, sort, setSort }: IProps) => {
  const [updateValues, setUpdateValues] = useState<ILanguageBaseWithId | null>(null)
  const { mutateAsync: removeLanguages } = useRemoveLanguages()
  const { data: user } = useMe()
  const theme = useTheme()
  const { selectedItemsIds, handleItemCheck, isItemSelected, setSelectedItemsIds, isAllUnchecked } =
    useTableItemsCheck({
      items: items ?? [],
    })

  const handleItemsRemove = async () => {
    if (!selectedItemsIds.length) return

    await removeLanguages(selectedItemsIds)
    setSelectedItemsIds([])
  }

  const handleSortChange = (sortName: string) => () => {
    setSort((prevSort) =>
      prevSort.split('-')[1] === 'asc' ? `${sortName}-desc` : `${sortName}-asc`,
    )
  }

  const handleUpdate = (item: ILanguageBaseWithId) => () => {
    setUpdateValues(item)
  }

  const handleClose = () => {
    setUpdateValues(null)
  }

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ 'td, th': { border: 0 } }}>
              <TableCell align="center" />
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('name')}
                  active={sort.split('-')[0] === 'name'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="impact-periods-name-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('code')}
                  active={sort.split('-')[0] === 'code'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="table-code-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <HelperWrapper t="enabled-checkbox-label" />
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('created_at')}
                  active={sort.split('-')[0] === 'created_at'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="table-created-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('updated_at')}
                  active={sort.split('-')[0] === 'updated_at'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="table-modified-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <IconButton disabled={isAllUnchecked} onClick={handleItemsRemove}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items?.map((item) => (
              <TableRow
                key={item.id}
                sx={{ 'td, th': { border: 0 } }}
                style={{
                  borderTop: `1px solid ${theme.palette.custom.grey}`,
                  borderBottom: `1px solid ${theme.palette.custom.grey}`,
                }}
              >
                <TableCell>
                  <Checkbox
                    disabled={item.code === DEFAULT_LANGUAGE_CODE}
                    checked={isItemSelected(item.id)}
                    onChange={handleItemCheck(item.id)}
                    sx={{ color: theme.palette.custom.grey2 }}
                  />
                </TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.code}</TableCell>
                <TableCell>
                  <Checkbox checked={item.enabled} disabled />
                </TableCell>
                <TableCell>
                  {getTableTimestamp({ date: item.created_at, timezone: user?.timezone })}
                </TableCell>
                <TableCell>
                  {getTableTimestamp({ date: item.updated_at, timezone: user?.timezone })}
                </TableCell>
                <TableCell align="center">
                  <Button
                    onClick={handleUpdate(item)}
                    disabled={item.code === DEFAULT_LANGUAGE_CODE}
                  >
                    <HelperWrapper t="update-button-text" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <AddLanguageButton />
      <UpdateLanguageModal
        initialValues={updateValues}
        open={!!updateValues}
        handleClose={handleClose}
      />
    </>
  )
}

export { LanguagesTable }
